import axios from "axios";
import BaseAPIs from "./base.apis";
import { iApiBasicResponse, iPlan, iShopDetails } from "../types/api";
import { iShopifyShop } from "../types/internal";
import { iFetchUserResponse } from "./user.apis";
// import { platform } from "os";
// import { useState } from "react";

export interface iRegisterFormResponse extends iApiBasicResponse {
  shop?: iShopifyShop;
}

export interface iStepOneResponse extends iApiBasicResponse {
  store?: iShopDetails;
  plans?: iPlan[];
  currencies?: string[];
  user?: iFetchUserResponse["user"];
  shops?: iFetchUserResponse["shops"];
  sessionEstablished?: boolean;
  onboardingResume?: boolean;
  tokenUpgraded?: boolean;
  reactURL?: string;
  redirectURL?: string;
}

export type StepOneStreamEvent =
  | { event: "progress"; phase: string; message?: string }
  | { event: "redirect"; data: { redirectURL: string; message?: string } }
  | { event: "session"; data: Partial<iStepOneResponse> & Record<string, unknown> }
  | { event: "store"; data: Partial<iShopDetails> & Record<string, unknown> }
  | { event: "plans"; data: { plans: iPlan[]; currencies: string[] } }
  | { event: "ai"; data: OnboardingAiData }
  | { event: "ai_error"; message: string }
  | { event: "done"; data: iStepOneResponse & Record<string, unknown> }
  | { event: "error"; message: string; fatal?: boolean };

export type StepOneStreamHandler = (event: StepOneStreamEvent) => void;

export type OnboardingAiData = NonNullable<iShopDetails["ai_data"]>;

export interface ifetchWcAddress extends iApiBasicResponse {
  address1?: any;
  statusCode: number;
  address2?: string;
  city?: string;
  postcode?: string;
  country?: string;
}
export interface iFetchWcLogoResponse extends iApiBasicResponse {
  logo?: any;
}

export interface iSimpleImageUploadResponse extends iApiBasicResponse {
  url?: string;
}

export interface iCouponResponse extends iApiBasicResponse {
  plan?: iPlan[];
}

export interface paypalApprovalUrl extends iApiBasicResponse {
  approval_url?: string;
}
export interface iWooCommerce extends iApiBasicResponse {
  plans?: iPlan[];
  currencies?: string[];
}
export interface iChangePlanResponse extends iApiBasicResponse {
  redirectURL?: string;
}
export type tStorePlatform = {
  [key: string]: any;
};

export const storePlatform = {
  currency: "",
  primary_currency: "",
  email: "",
  myshopify_domain: "",
  shop_owner_email: "",
  name: "",
  owner_first_name: "",
  owner_last_name: "",
  password: "",
  password_required: false,
  primary_domain: "",
  selected_plan_id: 0,
  selected_plan_price: 0,
  language: "en",
  font_family: "Open Sans",
  primary_background_color: "#000000ff",
  primary_text_color: "#ffffffff",
  secondary_background_color: "#ffffffff",
  secondary_text_color: "#000000ff",
  logo_path: "",
  full_name: "",
  phone: "",
  facebook: "",
  instagram: "",
  linkedin: "",
  twitter: "",
  skype: "",
  website: "",
  designation: "",
  logo: "",
  mode: "basic",
  demo_plan_id: 19,
  is_demo_shop: false,
  api_key: "",
  api_secret: "",
  isShopify: true,
  platform: "",
  address1: "",
  address2: "",
  city: "",
  postcode: "",
  country: "",
  coupon: "",
};

export interface iOnboardingFlagsResponse extends iApiBasicResponse {
  force_onboarding?: boolean;
  onboarding_step_1?: boolean;
  onboarding_step_2?: boolean;
  onboarding_step_3?: string | number | null | undefined;
  onboarding_step_4?: boolean;
  onboarding_step_5?: boolean;
  onboarding_step_6?: boolean;
  onboarding_step_7?: boolean;
  onboarding_step_8?: boolean;
  onboarding_completed?: boolean;
  allow_db_notifications?: boolean;
  allow_email_notifications?: boolean;
  domain_warning_mailed?: boolean;
  shop_synced_mailed?: boolean;
}

// Single template item
export interface iTemplateItem {
  id: number;
  name: string;
  preview_image: string;
  template_data: string | HTMLElement;
}

// API response
export interface iTemplatesResponse {
  headers: iTemplateItem[];
  footers: iTemplateItem[];
  banners: Array<number | string>;
}

export default class OnBoardingApis extends BaseAPIs {
  private urlEncodedBody(fields: Record<string, string>): URLSearchParams {
    const body = new URLSearchParams();
    Object.entries(fields).forEach(([key, value]) => {
      body.set(key, value ?? "");
    });
    return body;
  }

  forgot_password = async (email: string): Promise<iRegisterFormResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    return axios
      .post(
        this.getApiBaseURL() + "/password/email",
        {
          email: email,
          _token: csrfToken,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      )
      .then(
        (res): iRegisterFormResponse => ({
          statusCode: res.status,
          ...res.data,
        })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  reset_password = async (values: any): Promise<iRegisterFormResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    values["_token"] = csrfToken;
    return axios
      .post(this.getApiBaseURL() + "/password/reset", values, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      })
      .then(
        (res): iRegisterFormResponse => ({
          statusCode: res.status,
          ...res.data,
        })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  change_plan = async (
    myshopify_domain: string,
    selected_plan_id: number
  ): Promise<iChangePlanResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    let fd = new FormData();
    fd.set("myshopify_domain", myshopify_domain);
    fd.set("selected_plan_id", selected_plan_id.toString());
    fd.set("_token", csrfToken);
    return axios
      .post(this.getApiBaseURL() + "/onboarding/change_plan", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iApiBasicResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  register_form = async (
    domain: string,
    platform: string,
    api_key: string,
    api_secret: string,
    email: string
  ): Promise<iRegisterFormResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    const body = this.urlEncodedBody({
      myshopify_domain: domain,
      platform,
      api_key,
      api_secret,
      email,
      _token: csrfToken,
    });
    return axios
      .post(this.getApiBaseURL() + "/onboarding/register_form", body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iRegisterFormResponse => ({
          statusCode: res.status,
          ...res.data,
        })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };

  register_woocommerce_form = async (
    domain: string,
    platform: string,
    api_key: string,
    api_secret: string,
    email: string
  ): Promise<iRegisterFormResponse> => {
    console.log("domain :: " + domain);
    console.log("platform :: " + platform);
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };
    
    let fd = new FormData();
    fd.set("myshopify_domain", domain);
    fd.set("platform", platform);
    fd.set("api_key", api_key);
    fd.set("api_secret", api_secret);
    fd.set("email", email);
    fd.set("_token", csrfToken);

    let response = await axios
      .post(this.getApiBaseURL() + "/_woocommerce/oauth", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      });

    return response?.data

    // return axios
    //     .post(this.getApiBaseURL() + "/onboarding/register_form", fd, {
    //         headers: {"Content-Type": "application/x-www-form-urlencoded"},
    //         withCredentials: true,
    //     })
    //     .then((res): iRegisterFormResponse => ({statusCode: res.status, ...res.data}))
    //     .catch((error): any => {
    //         console.log(error)
    //         return this.handleCatch(error);
    //     })
  };
  re_authorize = async (
    myshopify_domain: string
  ): Promise<iRegisterFormResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    const body = this.urlEncodedBody({
      myshopify_domain,
      _token: csrfToken,
    });
    return axios
      .post(this.getApiBaseURL() + "/onboarding/re_authorize", body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iRegisterFormResponse => ({
          statusCode: res.status,
          ...res.data,
        })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  users_aff = async (af_id: string): Promise<iApiBasicResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    let fd = new FormData();
    fd.set("af_id", af_id);
    fd.set("_token", csrfToken);
    return axios
      .post(this.getApiBaseURL() + "/users/aff", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iApiBasicResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };

  step_one = async (
    platform: string,
    state: string,
    code: string,
    hmac: string,
    shop: string,
    timestamp: string,
    api_key: string,
    api_secret: string,
    secret: string,
    email: string,
    tracking_code: string,
    host: string = ""
  ): Promise<iStepOneResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    const body = this.urlEncodedBody({
      platform,
      api_key,
      api_secret,
      secret,
      email,
      state,
      code,
      hmac,
      shop,
      timestamp,
      host,
      _token: csrfToken,
      tracking_code: tracking_code || "",
    });
    return axios
      .post(this.getApiBaseURL() + "/onboarding/step_one", body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iStepOneResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };

  step_one_stream = async (
    platform: string,
    state: string,
    code: string,
    hmac: string,
    shop: string,
    timestamp: string,
    api_key: string,
    api_secret: string,
    secret: string,
    email: string,
    tracking_code: string,
    onEvent: StepOneStreamHandler,
    host: string = ""
  ): Promise<"done" | "redirect" | "error"> => {
    const csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken) {
      throw new Error("Network error!");
    }

    const body = this.urlEncodedBody({
      platform,
      api_key,
      api_secret,
      secret,
      email,
      state,
      code,
      hmac,
      shop,
      timestamp,
      host,
      _token: csrfToken,
      tracking_code: tracking_code || "",
    });

    const response = await fetch(this.getApiBaseURL() + "/onboarding/step_one", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/x-ndjson",
      },
      body,
      credentials: "include",
    });

    const contentType = response.headers.get("Content-Type") ?? "";

    if (contentType.includes("application/json") && !contentType.includes("ndjson")) {
      const data = (await response.json()) as iStepOneResponse & Record<string, unknown>;
      if (data.redirectURL) {
        onEvent({
          event: "redirect",
          data: { redirectURL: data.redirectURL, message: data.message },
        });
        return "redirect";
      }

      onEvent({ event: "done", data: { ...data, statusCode: response.status } });
      return "done";
    }

    if (!response.ok && !response.body) {
      throw new Error(`step_one stream failed: ${response.status}`);
    }

    if (!contentType.includes("application/x-ndjson")) {
      const fallbackBody = await response.text();
      let message = `step_one stream failed: ${response.status}`;

      try {
        const parsed = JSON.parse(fallbackBody) as { message?: string };
        if (parsed.message) {
          message = parsed.message;
        }
      } catch {
        if (fallbackBody.trim()) {
          message = fallbackBody.trim();
        }
      }

      throw new Error(message);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("step_one stream: empty response body");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let terminal: "done" | "redirect" | "error" | null = null;

    const dispatchLine = (line: string): void => {
      const trimmed = line.trim();
      if (!trimmed) {
        return;
      }

      let event: StepOneStreamEvent;
      try {
        event = JSON.parse(trimmed) as StepOneStreamEvent;
      } catch (parseError) {
        console.error("step_one stream: invalid NDJSON line", trimmed, parseError);
        return;
      }

      onEvent(event);

      if (event.event === "done") {
        terminal = "done";
      } else if (event.event === "redirect") {
        terminal = "redirect";
      } else if (event.event === "error" && event.fatal) {
        terminal = "error";
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          dispatchLine(line);
        }
      }

      dispatchLine(buffer);
    } catch (readError) {
      if (terminal !== null) {
        return terminal;
      }

      throw readError;
    }

    if (terminal === null) {
      throw new Error("step_one stream ended without a terminal event");
    }

    return terminal;
  };

  simple_image_upload = async (file: File): Promise<iSimpleImageUploadResponse> => {
    const csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    const fd = new FormData();
    fd.append("image", file);
    fd.append("_token", csrfToken);

    return axios
      .post(this.getApiBaseURL() + "/simple-image-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      })
      .then(
        (res): iSimpleImageUploadResponse => ({
          statusCode: res.status,
          ...res.data,
        })
      )
      .catch((error): any => this.handleCatch(error));
  };

  fetch_wc_logo = async (logo_url: string): Promise<iFetchWcLogoResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    let fd = new FormData();
    fd.set("logo_url", logo_url);
    fd.set("_token", csrfToken);
    return axios
      .post(this.getApiBaseURL() + "/onboarding/fetch_logo", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iFetchWcLogoResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  use_coupon = async (coupon: string): Promise<iCouponResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    let fd = new FormData();
    fd.set("_token", csrfToken);
    fd.set("coupon", coupon);
    return axios
      .post(this.getApiBaseURL() + "/onboarding/coupon", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then((res): iPlan => ({ statusCode: res.status, ...res.data }))
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };

  onboarding_abort = async (shop_id: string): Promise<iStepOneResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    let fd = new FormData();

    fd.set("_token", csrfToken);
    fd.set("shop", shop_id);
    return axios
      .post(this.getApiBaseURL() + "/onboarding/abort", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iStepOneResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  onboarding_settings = async (): Promise<iWooCommerce> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };
    let fd = new FormData();
    fd.set("_token", csrfToken);
    return axios
      .post(this.getApiBaseURL() + "/_woocommerce/onboarding_settings", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then((res): iWooCommerce => ({ statusCode: res.status, ...res.data }))
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  complete_plan = async (
    charge_id: string,
    shop: string
  ): Promise<iStepOneResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };

    let fd = new FormData();
    fd.set("shop", shop);
    fd.set("charge_id", charge_id);
    fd.set("_token", csrfToken);
    return axios
      .post(this.getApiBaseURL() + "/onboarding/complete_plan", fd, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        withCredentials: true,
      })
      .then(
        (res): iStepOneResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };

  select_plan = async (store: iShopDetails): Promise<iStepOneResponse> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };
    
    let fd = new FormData();
    fd.set("_token", csrfToken);
    fd.set("name", store.name || "");
    fd.set("primary_domain", store.primary_domain || "");
    fd.set("myshopify_domain", store.myshopify_domain || "");
    fd.set("font_family", store.font_family || "");
    // fd.set("primary_background_color", store.primary_background_color || "");
    // fd.set("primary_text_color", store.primary_text_color || "");
    fd.set("mode", store.mode);
    // fd.set(
    //   "secondary_background_color",
    //   store.secondary_background_color || ""
    // );
    // fd.set("secondary_text_color", store.secondary_text_color || "");
    fd.set("password", store.password || "");
    fd.set("owner_first_name", store.owner_first_name || "");
    fd.set("owner_last_name", store.owner_last_name || "");
    fd.set("currency", store.currency || "");
    fd.set("language", store.language || "en");
    fd.set("platform", store.platform || "shopify");
    fd.set("api_key", store.api_key || "");
    fd.set("api_secret", store.api_secret || "");
    fd.set("email", store.email || "");

    fd.set("phone", store.phone || "");
    fd.set("logo", store.logo || "");
    fd.set("facebook", store.facebook || "");
    fd.set("instagram", store.instagram || "");
    fd.set("linkedin", store.linkedin || "");
    fd.set("skype", store.skype || "");
    fd.set("twitter", store.twitter || "");
    fd.set("full_name", store.full_name || "");
    fd.set("designation", store.designation || "");
    fd.set("website", store.website || "");
    fd.set("address1", store.address1 || "");
    fd.set("city", store.city || "");
    fd.set("country", store.country);
    fd.set("shop_email", store.shop_email || "");

    // new data 
    fd.set("header", store.header || "");
    fd.set("footer", store.footer || "");
    fd.set("gen_logo", store?.gen_logo || "");
    // new primary_background_color, secondary_background_color, canvas_background_color 
    fd.set("primary_background_color", store.colors?.brandColor+"" || "");
    fd.set("primary_text_color", store.primary_text_color || "");
    fd.set("secondary_background_color", store.colors?.accentColor+"" || "");
    fd.set("secondary_text_color", store.secondary_text_color || "");
    fd.set("canvas_background_color", store.colors?.canvasColor+"" || "");
    fd.set("canvas_text_color", store.canvas_text_color || store.colors?.canvasColor+"" || "");
    fd.set("canvas_text_color", store.canvas_text_color || store.colors?.canvasColor+"" || "");

    // Handle the logo field based on its type (File or URL)
    if (store.logo) {
      if (typeof store.logo === "string") {
        try {
          const response = await fetch(store.logo);
          const blob = await response.blob();
          fd.set(
            "logo",
            new File([blob], `logo_${Date.now()}.png`, { type: blob.type })
          );
        } catch (error) {
          console.error("Error fetching logo URL:", error);
        }
      } else {
        fd.set("logo", store.logo);
      }
    }

    fd.set("selected_plan_id", store.selected_plan_id + "");
    fd.set("coupon", store.coupon || "");

    return axios
      .post(this.getApiBaseURL() + "/onboarding/select_plan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      })
      .then(
        (res): iStepOneResponse => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  paypal_approval_url = async (plan_id: string): Promise<paypalApprovalUrl> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999, errors: { network: "Network error!" } };
    return axios
      .get(this.getApiBaseURL() + "/_paypal/approval-url/" + plan_id)
      .then(
        (res): paypalApprovalUrl => ({ statusCode: res.status, ...res.data })
      )
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  stripe_checkout_session = async (plan_id: string): Promise<{ statusCode: number; checkout_url?: string }> => {
    let csrfToken: string = await new BaseAPIs().getCsrfToken();
    if (!csrfToken)
      return { statusCode: 999 };
    return axios
      .get(this.getApiBaseURL() + "/_stripe/checkout-session/" + plan_id, {
        withCredentials: true,
      })
      .then((res) => ({ statusCode: res.status, ...res.data }))
      .catch((error): any => {
        return this.handleCatch(error);
      });
  };
  getOnboardingFlags = async() : Promise<iOnboardingFlagsResponse> => {
    try {
      const response = await axios.get(
        this.getApiBaseURL() + "/onboarding/flags",
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      return {
        statusCode: response.status,
        ...response.data,
      };
    } catch (error) {
      return this.handleCatch(error);
    }
  };
  
  getSignupTemplates = async({category, language}: {category: string, language: string}) : Promise<iTemplatesResponse> => {
    try {
      const response = await axios.get<iTemplatesResponse>(
        this.getApiBaseURL() + "/onboarding/templates?category="+category+"&language="+language,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      return {
        ...response.data,
      };
    } catch (error) {
      return this.handleCatch(error);
    }
  }
}
